import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useDispatch } from "react-redux";
import type { User } from "@/store/slices/authSlice";
import { isSilentSsoEligible, runSilentSso } from "@/utils/silentSso";
import { resetUserScopedState } from "@/utils/sessionIdentity";

/**
 * Establish the OneView session from the current Microsoft (Entra ID) browser session.
 * See src/utils/silentSso.ts for the flow.
 *
 * @param enabled - attempt while there is no verified OneView session in this tab
 * @param persistedUser - the stored OneView user, if any; it is replaced when the
 *   Microsoft identity differs, and wiped when Microsoft cannot confirm anyone
 * @returns isChecking - true while the attempt (or a redirect) is in progress
 */
export const useSilentSso = (enabled: boolean, persistedUser: User | null = null) => {
  const { instance, inProgress } = useMsal();
  const dispatch = useDispatch();
  const [isDone, setIsDone] = useState(() => !isSilentSsoEligible());
  const persistedUserRef = useRef(persistedUser);
  persistedUserRef.current = persistedUser;

  useEffect(() => {
    if (!enabled || isDone || inProgress !== InteractionStatus.None) return;

    let isActive = true;
    runSilentSso(instance, dispatch, persistedUserRef.current).then((result) => {
      if (!isActive) return;
      // A stored session Microsoft cannot confirm must not be reused
      if (result === "unauthenticated" && persistedUserRef.current) {
        resetUserScopedState(dispatch);
      }
      // Keep the loader up while the browser navigates away for a redirect
      if (result !== "redirecting") setIsDone(true);
    });

    return () => {
      isActive = false;
    };
  }, [enabled, isDone, inProgress, instance, dispatch]);

  return { isChecking: enabled && !isDone };
};
